<template>
  <div
    class="header"
    @click="onClick"
    :class="[{ selected, isDone, nested }, action, cursor]"
    :style="coloring"
  >
    <button
      class="item-icon md-icon no-drag"
      ref="iconbtn"
      @click.stop="iconClick"
      @contextmenu.stop.prevent="typeMenu"
    >
      {{ item.getIcon() }}
    </button>
    <span class="item-name" @contextmenu="onContextmenu">
      <EditableTaskName
        v-if="editMode"
        :item="item"
        ref="taskName"
        @confirmed="stopEditing"
        @blur="unfocusEditing"
        @contextmenu.stop
      />
      <ItemTextFormatted v-else :item="item" />
    </span>
    <button
      v-if="!nested"
      class="item-icon md-icon menu no-drag"
      ref="menubtn"
      @click.stop="openMenu"
      @contextmenu.stop
    >
      more_horiz
    </button>
  </div>
</template>

<style scoped>
.header {
  min-height: var(--basic-card-size);
  display: flex;
  align-items: center;
  border-radius: var(--rounding-smaller);
}
.header.edit.cursor-edit2 {
  cursor: url("/public/cursor-edit2.png"), auto;
}
.header.edit.cursor-edit {
  cursor: url("/public/cursor-edit.png"), auto;
}
.header.selected {
  outline: var(--selection-border);
  outline-offset: -2px;
}
.card .header.selected,
.item-list .header.selected {
  outline: none;
}
.card .elements .header.selected {
  outline: var(--selection-border);
  outline-offset: -2px;
}
.list-header {
  background-color: var(--bg-color-list-header);
}

.insertMode .header {
  cursor: pointer;
}

span {
  white-space: pre-wrap;
}

.item-icon {
  margin: auto;
  min-height: 100%;
  flex: var(--indent-size) 0 0;
}
.no-icon .item-icon {
  display: none;
}
.no-menu .menu {
  display: none;
}
.item-name {
  word-wrap: break-word;
  overflow-wrap: anywhere;
  padding: 10px 0;
  flex: auto;
  min-width: var(--min-item-text-width);
}

.nested.header {
  font-size: var(--text-size-secondary);
  opacity: var(--text-opacity-secondary);
  height: unset;
  min-height: unset;
}
.nested .item-icon {
  flex: 0 0 var(--subitem-icon);
  font-size: 20px !important;
}
.nested .item-name {
  padding: 5px 0;
}
</style>

<script>
import selection from "../selection";
import settings from "../synced-settings";
import { getItem } from "../reactive-cache";
import { REL_PLAN, REL_ELEM, CONTAINER_TYPES } from "@/structure";
import { formatDateItem } from "@/time-utils";
import { inheritsCSSclass } from "@/utils";
import EditableTaskName from "./editable-task-name.vue";
import ItemTextFormatted from "./ItemTextFormatted.vue";

export default {
  name: "ItemHeader",
  // TODO discuss defaultAction prop - any smarter way?
  props: ["itemID", "target", "defaultAction"],
  components: { EditableTaskName, ItemTextFormatted },
  data() {
    return {
      isOpened: false,
      editMode: false,
    };
  },
  computed: {
    item() {
      return getItem(this.itemID);
    },
    selected() {
      return selection.has(this.item);
    },
    isDone() {
      return !!this.item.doneAtTimestampMs;
    },
    nested() {
      return !!this.target;
    },
    clickTarget() {
      return this.target || this.item;
    },
    coloring() {
      if (!this.nested) return "";
      let item = this.item;
      while (item) {
        if (item.color) {
          return "background-color: var(--card-color-" + item.color + ");";
        }
        item = item.getParent(REL_ELEM);
      }
      return "";
    },
    cursor() {
      return "cursor-" + settings.leafCursor;
    },
    action() {
      const hasChildren = !!this.clickTarget.getChildren().length;
      const isContainer = CONTAINER_TYPES.includes(this.clickTarget.type);
      if (this.clickTarget._id.startsWith("day:")) return "none";
      else if (inheritsCSSclass(this.$el, "split-panel")) return "edit";
      else if (this.clickTarget.readOnly) return "open";
      else if (this.defaultAction === "open") return "open";
      else if (this.isOpened) return "edit";
      else if (hasChildren || isContainer) return "open";
      else return "edit";
    },
  },
  methods: {
    iconClick(event) {
      this.item.toggleDone();
      this.stopEditing();
    },
    onClick(event) {
      if (event.ctrlKey) {
        event.preventDefault();
        event.stopPropagation();
        if (!this.clickTarget.readOnly) {
          selection.toggle(this.clickTarget);
        }
        return;
      }
      if (selection.length) {
        if (selection.insertMode) {
          selection.appendTo(this.clickTarget);
        }
        return;
      }
      switch (this.action) {
        case "open":
          this.clickTarget.open(event);
          break;
        case "edit":
          this.startEditing();
          break;
      }
    },
    startEditing() {
      this.editMode = true;
      if (!inheritsCSSclass(this.$el, "list-header")) {
        this.$parent.edited = true;
        this.$root.editedCard = this.$parent;
        this.$root.backdrop = true;
      }
      this.$nextTick(() => {
        this.$refs?.taskName?.focus();
      });
    },
    unfocusEditing() {
      this.editMode = false;
    },
    stopEditing() {
      this.$parent.edited = false;
      this.editMode = false;
      this.$root.backdrop = false;
    },
    onContextmenu(event) {
      const target = event.shiftKey ? this.item : this.clickTarget;
      event.preventDefault();
      event.stopPropagation();
      if (inheritsCSSclass(this.$el, "sidebar")) {
        return;
      }
      if (settings.RMBselects || this.$root.isMobile) {
        if (!target.readOnly) {
          selection.toggle(target);
        }
      } else {
        if (this.nested) {
          this.$parent.openMenu(event);
        } else {
          this.openMenu(event);
        }
      }
    },
    openMenu() {
      if (selection.length && !this.item.readOnly) {
        selection.add(this.item);
      }
      this.stopEditing();
      const button = this.$refs.menubtn.getBoundingClientRect();
      const placement = inheritsCSSclass(this.$el, "list-header") ? "header" : "";
      this.$root.$emit("action-menu", { item: this.clickTarget, button, placement });
    },
    typeMenu() {
      this.stopEditing();
      const button = this.$refs.iconbtn.getBoundingClientRect();
      this.$root.$emit("type-menu", { item: this.item, button });
    },
  },
  mounted() {
    this.isOpened = inheritsCSSclass(this.$el, "main-panel")
      ? this.$route.params.itemID === this.clickTarget._id
      : this.$route.params.splitID === this.clickTarget._id;
  },
};
</script>
